import React, { useState } from "react";
import UserService from "rc-app/services/UserService";
import Icon from "rc-app/components/Icons";

type View = "login" | "forgot" | "forgot_success" | "first_login" | "first_login_success";

const LoginPage: React.FC = () => {
  const [view, setView] = useState<View>("login");

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotError, setForgotError] = useState<string | null>(null);
  const [forgotLoading, setForgotLoading] = useState(false);

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [firstLoginError, setFirstLoginError] = useState<string | null>(null);
  const [firstLoginLoading, setFirstLoginLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await UserService.loginWithCredentials(email, password);
    } catch (err: any) {
      if (err.code === "first_login_required") {
        setView("first_login");
      } else {
        setError(err.message || "Login failed. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setForgotError(null);
    setForgotLoading(true);
    try {
      const url = `${process.env.REACT_APP_ONEVERSE_REALESTATE_SERVER_URL}/api/v1/auth/forgot-password`;
      const response = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: forgotEmail }),
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data?.message || "Failed to send reset email.");
      }
      setView("forgot_success");
    } catch (err: any) {
      setForgotError(err.message || "Something went wrong. Please try again.");
    } finally {
      setForgotLoading(false);
    }
  };

  const handleFirstLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      setFirstLoginError("Passwords do not match.");
      return;
    }
    if (newPassword.length < 8) {
      setFirstLoginError("Password must be at least 8 characters.");
      return;
    }
    setFirstLoginError(null);
    setFirstLoginLoading(true);
    try {
      const url = `${process.env.REACT_APP_ONEVERSE_REALESTATE_SERVER_URL}/api/v1/auth/first-login-reset`;
      const response = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, temporaryPassword: password, newPassword }),
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data?.message || "Failed to set new password.");
      }
      setView("first_login_success");
    } catch (err: any) {
      setFirstLoginError(err.message || "Something went wrong. Please try again.");
    } finally {
      setFirstLoginLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#1C2C3A] to-[#1E4F52] flex flex-col items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="flex justify-center mb-8">
          <Icon name="OpenSkyLogoWithText" className="w-40 h-14" />
        </div>

        <div className="bg-[#161F28] rounded-2xl border border-[#374B5E] p-8 shadow-2xl">

          {/* LOGIN VIEW */}
          {view === "login" && (
            <>
              <h1 className="text-white text-2xl font-semibold mb-1">Sign in</h1>
              <p className="text-[#889DB2] text-sm mb-6">Enter your credentials to access your account</p>
              <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                <div className="flex flex-col gap-1.5">
                  <label className="text-[#889DB2] text-sm font-medium">Email</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    required
                    className="px-4 py-3 rounded-lg bg-[#1C2C3A] border border-[#374B5E] text-white placeholder-[#4A5C6A] text-sm outline-none focus:border-[#008564] focus:ring-1 focus:ring-[#008564] transition-colors"
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className="text-[#889DB2] text-sm font-medium">Password</label>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      required
                      className="w-full px-4 py-3 rounded-lg bg-[#1C2C3A] border border-[#374B5E] text-white placeholder-[#4A5C6A] text-sm outline-none focus:border-[#008564] focus:ring-1 focus:ring-[#008564] transition-colors pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((p) => !p)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[#889DB2] hover:text-white transition-colors text-xs"
                    >
                      {showPassword ? "Hide" : "Show"}
                    </button>
                  </div>
                  <div className="flex justify-end">
                    <button
                      type="button"
                      onClick={() => { setForgotEmail(""); setForgotError(null); setView("forgot"); }}
                      className="text-xs text-[#008564] hover:text-[#00a87d] transition-colors"
                    >
                      Forgot password?
                    </button>
                  </div>
                </div>
                {error && (
                  <div className="bg-red-500/10 border border-red-500/30 rounded-lg px-4 py-3 text-red-400 text-sm">
                    {error}
                  </div>
                )}
                <button
                  type="submit"
                  disabled={loading}
                  className="mt-2 w-full py-3 rounded-lg bg-[#008564] hover:bg-[#006b51] text-white font-semibold text-sm transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {loading ? "Signing in..." : "Sign In"}
                </button>
              </form>
            </>
          )}

          {/* FIRST LOGIN - SET NEW PASSWORD */}
          {view === "first_login" && (
            <>
              <div className="flex items-center gap-2 mb-1">
                <div className="w-2 h-2 rounded-full bg-[#008564]" />
                <span className="text-[#008564] text-xs font-medium uppercase tracking-wide">First time login</span>
              </div>
              <h1 className="text-white text-2xl font-semibold mb-1">Set your password</h1>
              <p className="text-[#889DB2] text-sm mb-6">
                Your account requires a new password before you can continue.
              </p>
              <form onSubmit={handleFirstLogin} className="flex flex-col gap-4">
                <div className="flex flex-col gap-1.5">
                  <label className="text-[#889DB2] text-sm font-medium">New Password</label>
                  <div className="relative">
                    <input
                      type={showNew ? "text" : "password"}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="••••••••"
                      required
                      className="w-full px-4 py-3 rounded-lg bg-[#1C2C3A] border border-[#374B5E] text-white placeholder-[#4A5C6A] text-sm outline-none focus:border-[#008564] focus:ring-1 focus:ring-[#008564] transition-colors pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowNew((p) => !p)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[#889DB2] hover:text-white transition-colors text-xs"
                    >
                      {showNew ? "Hide" : "Show"}
                    </button>
                  </div>
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className="text-[#889DB2] text-sm font-medium">Confirm Password</label>
                  <div className="relative">
                    <input
                      type={showConfirm ? "text" : "password"}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="••••••••"
                      required
                      className="w-full px-4 py-3 rounded-lg bg-[#1C2C3A] border border-[#374B5E] text-white placeholder-[#4A5C6A] text-sm outline-none focus:border-[#008564] focus:ring-1 focus:ring-[#008564] transition-colors pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirm((p) => !p)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[#889DB2] hover:text-white transition-colors text-xs"
                    >
                      {showConfirm ? "Hide" : "Show"}
                    </button>
                  </div>
                </div>
                {firstLoginError && (
                  <div className="bg-red-500/10 border border-red-500/30 rounded-lg px-4 py-3 text-red-400 text-sm">
                    {firstLoginError}
                  </div>
                )}
                <button
                  type="submit"
                  disabled={firstLoginLoading}
                  className="mt-2 w-full py-3 rounded-lg bg-[#008564] hover:bg-[#006b51] text-white font-semibold text-sm transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {firstLoginLoading ? "Setting password..." : "Set Password & Sign In"}
                </button>
              </form>
            </>
          )}

          {/* FIRST LOGIN SUCCESS */}
          {view === "first_login_success" && (
            <div className="flex flex-col items-center gap-4 py-4">
              <div className="w-14 h-14 rounded-full bg-[#008564]/20 flex items-center justify-center">
                <svg className="w-7 h-7 text-[#008564]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h1 className="text-white text-xl font-semibold">Password set!</h1>
              <p className="text-[#889DB2] text-sm text-center">
                Your account is ready. Sign in with your new password to get started.
              </p>
              <button
                type="button"
                onClick={() => { setNewPassword(""); setConfirmPassword(""); setPassword(""); setView("login"); }}
                className="mt-2 w-full py-3 rounded-lg bg-[#008564] hover:bg-[#006b51] text-white font-semibold text-sm transition-colors"
              >
                Sign In
              </button>
            </div>
          )}

          {/* FORGOT PASSWORD VIEW */}
          {view === "forgot" && (
            <>
              <h1 className="text-white text-2xl font-semibold mb-1">Reset password</h1>
              <p className="text-[#889DB2] text-sm mb-6">Enter your email and we'll send you a reset link.</p>
              <form onSubmit={handleForgotPassword} className="flex flex-col gap-4">
                <div className="flex flex-col gap-1.5">
                  <label className="text-[#889DB2] text-sm font-medium">Email</label>
                  <input
                    type="email"
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    placeholder="you@example.com"
                    required
                    className="px-4 py-3 rounded-lg bg-[#1C2C3A] border border-[#374B5E] text-white placeholder-[#4A5C6A] text-sm outline-none focus:border-[#008564] focus:ring-1 focus:ring-[#008564] transition-colors"
                  />
                </div>
                {forgotError && (
                  <div className="bg-red-500/10 border border-red-500/30 rounded-lg px-4 py-3 text-red-400 text-sm">
                    {forgotError}
                  </div>
                )}
                <button
                  type="submit"
                  disabled={forgotLoading}
                  className="mt-2 w-full py-3 rounded-lg bg-[#008564] hover:bg-[#006b51] text-white font-semibold text-sm transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {forgotLoading ? "Sending..." : "Send Reset Link"}
                </button>
                <button
                  type="button"
                  onClick={() => setView("login")}
                  className="text-sm text-[#889DB2] hover:text-white transition-colors text-center"
                >
                  ← Back to sign in
                </button>
              </form>
            </>
          )}

          {/* FORGOT SUCCESS VIEW */}
          {view === "forgot_success" && (
            <div className="flex flex-col items-center gap-4 py-4">
              <div className="w-14 h-14 rounded-full bg-[#008564]/20 flex items-center justify-center">
                <svg className="w-7 h-7 text-[#008564]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h1 className="text-white text-xl font-semibold">Check your email</h1>
              <p className="text-[#889DB2] text-sm text-center">
                We've sent a password reset link to <span className="text-white">{forgotEmail}</span>.
                Check your inbox and follow the instructions.
              </p>
              <button
                type="button"
                onClick={() => setView("login")}
                className="mt-2 w-full py-3 rounded-lg bg-[#008564] hover:bg-[#006b51] text-white font-semibold text-sm transition-colors"
              >
                Back to sign in
              </button>
            </div>
          )}

        </div>

        <p className="text-center mt-4">
          <a href="/" className="text-[#889DB2] text-sm hover:text-white transition-colors">← Back to home</a>
        </p>
        <p className="text-center text-[#889DB2] text-xs mt-3">
          © {new Date().getFullYear()} OpenSky. All rights reserved.
        </p>
      </div>
    </div>
  );
};

export default LoginPage;
